import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import "./App.css";
import BrandName from './components/BrandName';
import { 
  trackPageView, 
  trackQuoteSubmission, 
  trackOrderCreation, 
  trackAddToCart, 
  trackDesignView,
  trackContactFormSubmission,
  trackServiceRequest,
  trackUserAuth,
  trackDesignerApplication,
  trackDesignUpload
} from './utils/analytics';
import { Upload, FileText, Zap, Settings, ChevronRight, ChevronDown, Check, CheckCircle, Mail, Phone, MapPin, ArrowLeft, User, Users, LogIn, ShoppingCart, Package, Eye, Edit, X } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider, useCurrency } from "./contexts/CurrencyContext";
import { CartProvider, useCart } from "./contexts/CartContext";
import Profile from "./components/Profile";
import LoginModal from "./components/LoginModal";
import MainNavigation from "./components/MainNavigation";
import ProductsPage from "./components/ProductsPage";
import Footer from "./components/Footer";
import ProductDetailPage from "./components/ProductDetailPage";
import AdminPanel from "./components/AdminPanel";
import CartPage from "./components/CartPage";
import ThreeDPreview from "./components/ThreeDPreview";
import SharedPageLayout from "./components/SharedPageLayout";
import ComingSoon from "./components/ComingSoon";
import CurrencySelector from "./components/CurrencySelector";
import { BACKEND_URL } from "./config";
import { termsOfService, shippingReturns, licensingPolicy, privacyPolicy } from "./data/policies";
import { TermsOfServiceRoute, ShippingReturnsRoute, LicensingPolicyRoute, PrivacyPolicyRoute } from "./pages/PolicyPages";
import { FAQRoute, TermsRoute, FileFormatsRoute, MaterialGuideRoute, QualityStandardsRoute, ShippingInfoRoute } from "./pages/InfoPages";
import { ProductsRoute, ProductDetailRoute, CartRoute, ProfileRoute, AdminRoute, AboutRoute } from "./pages/SimpleRoutes";
import { ServicesRoute, ContactRoute } from "./pages/FeatureRoutes";
import { AdminMarketplaceRoute, MarketplaceRoute, ListingDetailRoute, DesignerEditRoute } from "./pages/MarketplaceRoutes";
import { DesignerApplyRoute, DesignerDashboardRoute, DesignerUploadRoute, DesignerTermsRoute, CustomerLicenseTermsRoute } from "./pages/DesignerRoutes";
import { ComingSoonPage, AppContent, CreatorProfileRoute } from "./pages/HomePage";

// Use runtime-detected backend URL (handles different deployment environments)
const API_BASE = BACKEND_URL;
const API = `${API_BASE}/api`;

// Google Analytics Page View Tracker
const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    // Track page view on route change
    trackPageView(location.pathname + location.search, document.title);
  }, [location]);

  return null; // This component doesn't render anything
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if coming soon mode is enabled
    const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'true';
    if (!comingSoonEnabled) return true; // If not in coming soon mode, allow access
    
    // Check if already authenticated (persists across sessions)
    return localStorage.getItem('site_authenticated') === 'true';
  });

  const handleAuthenticate = () => {
    setIsAuthenticated(true);
  };

  // Check if coming soon mode is enabled
  const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'true';

  // If coming soon is enabled and user is not authenticated, show password page
  if (comingSoonEnabled && !isAuthenticated) {
    return <ComingSoon onAuthenticated={handleAuthenticate} />;
  }

  // Otherwise, show the full app
  return (
    <Router>
      <PageViewTracker />
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
            <CurrencySelector />
            <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/products" element={<ProductsRoute />} />
            <Route path="/product/:slug" element={<ProductDetailRoute />} />
            <Route path="/cart" element={<CartRoute />} />
            <Route path="/profile" element={<ProfileRoute />} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="/about" element={<AboutRoute />} />
            <Route path="/services" element={<ServicesRoute />} />
            <Route path="/contact" element={<ContactRoute />} />
            <Route path="/marketplace" element={<MarketplaceRoute />} />
            <Route path="/marketplace/listing/:id" element={<ListingDetailRoute />} />
            <Route path="/creator/:username" element={<CreatorProfileRoute />} />
            <Route path="/designer/apply" element={<DesignerApplyRoute />} />
            <Route path="/designer/dashboard" element={<DesignerDashboardRoute />} />
            <Route path="/designer/upload" element={<DesignerUploadRoute />} />
            <Route path="/designer/edit/:id" element={<DesignerEditRoute />} />
            <Route path="/designer/terms" element={<DesignerTermsRoute />} />
            <Route path="/customer/license-terms" element={<CustomerLicenseTermsRoute />} />
            <Route path="/admin/marketplace" element={<AdminMarketplaceRoute />} />
            <Route path="/faq" element={<FAQRoute />} />
            <Route path="/terms" element={<TermsRoute />} />
            <Route path="/file-formats" element={<FileFormatsRoute />} />
            <Route path="/material-guide" element={<MaterialGuideRoute />} />
            <Route path="/quality-standards" element={<QualityStandardsRoute />} />
            <Route path="/shipping-info" element={<ShippingInfoRoute />} />
            
            {/* Policy Pages */}
            <Route path="/terms-of-service" element={<TermsOfServiceRoute />} />
            <Route path="/shipping-returns" element={<ShippingReturnsRoute />} />
            <Route path="/licensing-policy" element={<LicensingPolicyRoute />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </Router>
  );
};

// Utility function to create SEO-friendly slugs
const createProductSlug = (manufacturer, productName) => {
  const combined = `${manufacturer} ${productName}`;
  return combined
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim('-'); // Remove leading/trailing hyphens
};

// Utility function to parse slug back to search terms
const parseProductSlug = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Cart Context moved to /contexts/CartContext.js
// HomePage components (ComingSoonPage, AppContent, CreatorProfileRoute) moved to /pages/HomePage.js
// Designer routes (Apply, Dashboard, Upload, Terms, CustomerLicenseTerms) moved to /pages/DesignerRoutes.js

export default App;